import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectDropdownProps<T> {
    options: T[];
    onSelect: (value: string) => void;
    getValue: (option: T) => string;
    getDisplayText: (option: T) => string;
    getGroup?: (option: T) => string;
    placeholder?: string;
    dropdownContainerClassName?: string;
    isValidOption?: (option: T) => boolean;
    selectedValue?: string;
    testId?: string;
    disabled?: boolean;
}

const SelectDropdown = <T,>({
    options,
    onSelect,
    getValue,
    getDisplayText,
    getGroup,
    placeholder = 'Select an option',
    dropdownContainerClassName = '',
    isValidOption = () => true,
    selectedValue = '',
    testId = 'select',
    disabled = false,
}: SelectDropdownProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<HTMLUListElement>(null);
    const highlightedOptionRef = useRef<HTMLLIElement>(null);

    // Filter options based on validity
    const filteredOptions = options.filter(option => isValidOption(option));

    // Grouped options logic
    type GroupedOptions = { [group: string]: T[] };
    const groupedOptions: GroupedOptions = getGroup
        ? filteredOptions.reduce((acc, option) => {
            const group = getGroup(option);
            if (!acc[group]) acc[group] = [];
            acc[group].push(option);
            return acc;
        }, {} as GroupedOptions)
        : { '': filteredOptions };
    // Flattened list for keyboard navigation
    const flatOptions: { option: T; group: string; isGroupHeader: boolean }[] = [];
    Object.entries(groupedOptions).forEach(([group, opts]) => {
        if (group) {
            flatOptions.push({ option: null as any, group, isGroupHeader: true });
        }
        opts.forEach(option => {
            flatOptions.push({ option, group, isGroupHeader: false });
        });
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Scroll highlighted option into view
    useEffect(() => {
        if (isOpen && highlightedIndex >= 0 && highlightedOptionRef.current && optionsRef.current) {
            const optionElement = highlightedOptionRef.current;
            const containerElement = optionsRef.current;

            const optionTop = optionElement.offsetTop;
            const optionBottom = optionTop + optionElement.offsetHeight;
            const containerTop = containerElement.scrollTop;
            const containerBottom = containerTop + containerElement.offsetHeight;

            if (optionTop < containerTop) {
                containerElement.scrollTop = optionTop;
            } else if (optionBottom > containerBottom) {
                containerElement.scrollTop = optionBottom - containerElement.offsetHeight;
            }
        }
    }, [highlightedIndex, isOpen]);

    // Set initial highlight to selected option when opening dropdown
    useEffect(() => {
        if (isOpen && selectedValue) {
            const selectedIndex = flatOptions.findIndex(
                (item) => !item.isGroupHeader && getValue(item.option) === selectedValue
            );
            if (selectedIndex >= 0) {
                setHighlightedIndex(selectedIndex);
            }
        }
    }, [isOpen, selectedValue, options, getValue]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                if (selectedValue) {
                    const selectedIndex = flatOptions.findIndex(
                        (item) => !item.isGroupHeader && getValue(item.option) === selectedValue
                    );
                    if (selectedIndex >= 0) {
                        setHighlightedIndex(selectedIndex);
                    }
                }
            }
            return;
        }

        const nextIndex = (start: number, dir: 1 | -1) => {
            let idx = start;
            do {
                idx = (idx + dir + flatOptions.length) % flatOptions.length;
            } while (flatOptions[idx]?.isGroupHeader && idx !== start);
            return idx;
        };
        switch (e.key) {
            case 'ArrowUp': {
                e.preventDefault();
                if (flatOptions.length === 0) return;
                let newIndex = highlightedIndex;
                do {
                    newIndex = nextIndex(newIndex, -1);
                } while (flatOptions[newIndex]?.isGroupHeader && newIndex !== highlightedIndex);
                setHighlightedIndex(newIndex);
                break;
            }
            case 'ArrowDown': {
                e.preventDefault();
                if (flatOptions.length === 0) return;
                let newIndex = highlightedIndex;
                do {
                    newIndex = nextIndex(newIndex, 1);
                } while (flatOptions[newIndex]?.isGroupHeader && newIndex !== highlightedIndex);
                setHighlightedIndex(newIndex);
                break;
            }
            case 'Enter':
            case ' ': {
                e.preventDefault();
                if (
                    highlightedIndex >= 0 &&
                    highlightedIndex < flatOptions.length &&
                    !flatOptions[highlightedIndex].isGroupHeader
                ) {
                    selectOption(flatOptions[highlightedIndex].option);
                }
                break;
            }
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            // Allow to search dropdown lists by using keyboard input
            default: {
                if (e.key.length === 1 && e.key.match(/[a-zA-Z]+/g)) {
                    e.preventDefault();

                    const letter = e.key.toLowerCase();
                    // Try to find next match AFTER current highlighted index
                    let matchedIndex = flatOptions.findIndex((item: any, index: number) => {
                        return index > highlightedIndex &&
                            !item.isGroupHeader &&
                            getDisplayText(item.option).toLowerCase().startsWith(letter);
                    });
                    // If no match after current position, wrap to beginning
                    if (matchedIndex === -1) {
                        matchedIndex = flatOptions.findIndex((item) =>
                            !item.isGroupHeader &&
                            getDisplayText(item.option).toLowerCase().startsWith(letter)
                        );
                    }

                    if (matchedIndex >= 0) {
                        setHighlightedIndex(matchedIndex);
                    }
                }
            }
        }
    };

    // Handle mouse enter on option
    const handleMouseEnter = (index: number) => {
        setHighlightedIndex(index);
    };

    // Select option
    const selectOption = (option: T) => {
        onSelect(getValue(option));
        setIsOpen(false);
        setHighlightedIndex(-1);
    };

    // Find the selected option for display
    const selectedOption = options.find(option => getValue(option) === selectedValue);

    return (
        <div
            className="relative w-full"
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            id={`${testId}-container`}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${testId}-options-list`}
        >
            {/* Selected Option Display */}
            <div
                id={`${testId}-trigger`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full px-3 py-2 border border-border rounded-md flex items-center ${disabled ? 'cursor-not-allowed bg-gray-100' : 'cursor-pointer'} focus:outline-none focus:ring-2 focus:ring-black focus:border-black z-9 bg-white`}
            >
                <span
                    id={`${testId}-selected-value`}
                    className={`text-sm ${!selectedValue || !selectedOption ? 'text-gray-500' : disabled ? 'text-gray-400' : 'text-gray-700'} mr-1.5`}
                >
                    {selectedOption ? getDisplayText(selectedOption) : placeholder}
                </span>
                <ChevronDown
                    id={`${testId}-chevron`}
                    className={`w-4 h-4 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${disabled ? 'text-gray-400' : ''}`}
                />
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div
                    id={`${testId}-dropdown`}
                    className={`absolute left-0 right-0 bg-white border border-border rounded-md mt-1 shadow-lg z-10 ${dropdownContainerClassName}`}
                >
                    {/* Options List */}
                    <ul
                        ref={optionsRef}
                        className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
                        role="listbox"
                        id={`${testId}-options-list`}
                        aria-label="Options"
                    >
                        {flatOptions.length === 0 ? (
                            <li
                                id={`${testId}-no-options`}
                                className="px-3 py-1 text-gray-500 italic truncate text-start text-sm"
                            >
                                No options available
                            </li>
                        ) : (
                            flatOptions.map((item, index) => {
                                if (item.isGroupHeader) {
                                    return (
                                        <li key={`group-${item.group}`} className="px-3 py-2 text-sm text-gray-500 bg-gray-50 sticky top-0 z-10">
                                            {item.group}
                                        </li>
                                    );
                                }
                                const value = getValue(item.option);
                                const isSelected = selectedValue === value;
                                const isHighlighted = index === highlightedIndex;
                                const isGrouped = item.group !== '';
                                return (
                                    <li
                                        key={value}
                                        ref={isHighlighted ? highlightedOptionRef : null}
                                        id={`${testId}-option-${index}`}
                                        onClick={() => selectOption(item.option)}
                                        onMouseEnter={() => handleMouseEnter(index)}
                                        className={`py-1 cursor-pointer truncate whitespace-nowrap text-start flex justify-between text-sm transition-colors duration-150
                                            ${isGrouped ? 'pl-6 pr-3' : 'px-3'}
                                            ${isHighlighted ? 'bg-gray-200' : ''}
                                            hover:bg-gray-200
                                        `}
                                        role="option"
                                        aria-selected={isSelected}
                                        aria-current={isHighlighted}
                                    >
                                        {getDisplayText(item.option)}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SelectDropdown;
